import { create } from 'zustand';
import type { Project, ProjectDetail } from '@/lib/schemas';

interface ProjectState {
    projects: Project[];
    currentProject: ProjectDetail | null;
    isLoading: boolean;
    error: string | null;

    // Actions
    setProjects: (projects: Project[]) => void;
    addProject: (project: Project) => void;
    updateProject: (projectId: string, updates: Partial<Project>) => void;
    removeProject: (projectId: string) => void;
    setCurrentProject: (project: ProjectDetail | null) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    reset: () => void;
}

const initialState = {
    projects: [],
    currentProject: null,
    isLoading: false,
    error: null,
};

export const useProjectStore = create<ProjectState>()((set) => ({
    ...initialState,

    setProjects: (projects) =>
        set({ projects, isLoading: false, error: null }),

    addProject: (project) =>
        set((state) => ({
            projects: [project, ...state.projects],
        })),

    updateProject: (projectId, updates) =>
        set((state) => ({
            projects: state.projects.map((p) =>
                p.id === projectId ? { ...p, ...updates } : p
            ),
            currentProject:
                state.currentProject?.id === projectId
                    ? { ...state.currentProject, ...updates }
                    : state.currentProject,
        })),

    removeProject: (projectId) =>
        set((state) => ({
            projects: state.projects.filter((p) => p.id !== projectId),
            currentProject:
                state.currentProject?.id === projectId ? null : state.currentProject,
        })),

    setCurrentProject: (project) =>
        set({ currentProject: project }),

    setLoading: (loading) =>
        set({ isLoading: loading }),

    setError: (error) =>
        set({ error, isLoading: false }),

    reset: () => set(initialState),
}));
